import React, { useEffect, useState, useMemo, useCallback } from "react";
import { forwardRef } from "react";
import {
  Button,
  Table,
  Pagination,
  Navbar,
  Dropdown,
  Form,
} from "react-bootstrap";
import { IconPencilFill, IconDelete } from "../components/SVGs";
import { LoadingScreens } from "./LoadingScreens";
import Select from "react-select";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";

// Constants
const CACHE_KEYS = {
  RXN_SEARCH: 'rxnSearch',
  RXN_TAG: 'rxnTag',
  ADMIN_RXN_SEARCH: 'adminRxnSearch',
  ADMIN_RXN_TAG: 'adminRxnTag',
  SYN_SEARCH: 'synSearch',
};

const PER_PAGE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PER_PAGE = 20;

const RxnTable = forwardRef((props, ref) => {
  const {
    bordered = false,
    striped = false,
    hover = false,
    token = null,
    data = { data: [], columns: [] },
    tags = [],
    hasTags = false,
    rxnCache = false,
    synCache = false,
    adminRxnCache = false,
    actions = null,
    loading = false,
  } = props;

  // Memoized initial values
  const initialTagCache = useMemo(() => {
    if (!hasTags) return [];

    const cacheKey = adminRxnCache ? CACHE_KEYS.ADMIN_RXN_TAG : CACHE_KEYS.RXN_TAG;
    if ((rxnCache || adminRxnCache)) {
      const cached = localStorage.getItem(cacheKey);
      return cached ? JSON.parse(cached) : [];
    }
    return [];
  }, [hasTags, rxnCache, adminRxnCache]);

  const initialSearch = useMemo(() => {
    if (rxnCache) return localStorage.getItem(CACHE_KEYS.RXN_SEARCH) || "";
    if (adminRxnCache) return localStorage.getItem(CACHE_KEYS.ADMIN_RXN_SEARCH) || "";
    if (synCache) return localStorage.getItem(CACHE_KEYS.SYN_SEARCH) || "";
    return "";
  }, [rxnCache, adminRxnCache, synCache]);

  // State
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [tagCache, setTagCache] = useState(initialTagCache);
  const [search, setSearch] = useState(initialSearch);
  const [sortConfig, setSortConfig] = useState([]);

  // Memoized filtered data
  const filteredData = useMemo(() => {
    if (!data?.data) return [];

    if (search === "" && tagCache.length === 0) {
      return data.data;
    }

    return data.data.filter((item) => {
      const matchesSearch = search === "" ||
        data.columns.some(column =>
          String(item[column.field] || "")
            .toLowerCase()
            .includes(search.toLowerCase())
        );

      const matchesTags = tagCache.length === 0 ||
        tagCache.every(tag => item.tags?.includes(tag.label));

      return matchesSearch && matchesTags;
    });
  }, [data, search, tagCache]);

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / perPage);

  // If Sortable, then sort data in columns alphabetically
  const sortedData = useMemo(() => {
    const sortable = [...filteredData];
    if (!sortConfig.length) return sortable;

    sortable.sort((a, b) => {
      for (const { key, direction } of sortConfig) {
        const aValue = a[key];
        const bValue = b[key];

        if (aValue == null && bValue != null) return direction === 'asc' ? -1 : 1;
        if (aValue != null && bValue == null) return direction === 'asc' ? 1 : -1;
        if (aValue == null && bValue == null) continue;

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          if (aValue !== bValue) return direction === 'asc' ? aValue - bValue : bValue - aValue;
        } else {
          const result = String(aValue).localeCompare(String(bValue));
          if (result !== 0) return direction === 'asc' ? result : -result;
        }
      }
      return 0;
    });

    return sortable;
  }, [filteredData, sortConfig]);

  // Memoized paginated data
  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * perPage;
    return sortedData.slice(startIndex, startIndex + perPage);
  }, [sortedData, page, perPage]);

  // Pagination logic
  const paginationItems = useMemo(() => {
    if (totalPages <= 1) return [];

    const items = [];
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, page + 2);

    // Adjust range for edge cases
    if (page <= 2) {
      end = Math.min(5, totalPages);
    }
    if (page > totalPages - 2) {
      start = Math.max(1, totalPages - 4);
    }

    for (let i = start; i <= end; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === page}
          onClick={() => setPage(i)}
          aria-label={`Go to page ${i}`}
        >
          {i}
        </Pagination.Item>
      );
    }
    return items;
  }, [page, totalPages]);

  // Event handlers
  const handlePerPageChange = useCallback((newPerPage) => {
    setPerPage(newPerPage);
    setPage(1); // Reset to first page when changing per page
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page when searching
  }, []);

  const handleTagChange = useCallback((selectedTags) => {
    setTagCache(selectedTags || []);
    setPage(1); // Reset to first page when filtering
  }, []);

  const handleRowClick = useCallback((row) => {
    if (actions?.view && actions?.viewLogic) {
      actions.viewLogic(row);
    }
  }, [actions]);

  const handleSort = (field) => {
    setSortConfig((prev) => {
      const existing = prev.find((s) => s.key === field);
      if (existing) {
        // Toggle direction or remove
        if (existing.direction === 'asc') {
          return prev.map((s) =>
            s.key === field ? { ...s, direction: 'desc' } : s
          );
        } else {
          // Remove sort if descending already
          return prev.filter((s) => s.key !== field);
        }
      } else {
        // Add new sort key
        return [...prev, { key: field, direction: 'asc' }];
      }
    });
  };


  // Table cell renderer
  const renderTableCells = useCallback((row, rowIndex) => {
    return data.columns.map((column, colIndex) => (
      <td key={`cell-${rowIndex}-${colIndex}`}>
        {row[column.field] || ""}
      </td>
    ));
  }, [data.columns]);

  // Action buttons renderer
  const renderActionButtons = useCallback((row, rowIndex) => {
    if (!actions || (!actions.edit && !actions.delete)) return null;

    const buttons = [];

    if (actions.edit && actions.editLogic) {
      buttons.push(
        <Button
          key={`edit-${rowIndex}`}
          className="m-1"
          variant="outline-primary"
          size="sm"
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click
            actions.editLogic(row);
          }}
          aria-label="Edit row"
        >
          <IconPencilFill />
        </Button>
      );
    }

    if (actions.delete && actions.deleteLogic) {
      buttons.push(
        <Button
          key={`delete-${rowIndex}`}
          className="m-1"
          variant="outline-danger"
          size="sm"
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click
            actions.deleteLogic(row);
          }}
          aria-label="Delete row"
        >
          <IconDelete />
        </Button>
      );
    }

    return <td key={`actions-${rowIndex}`} className="action">{buttons}</td>;
  }, [actions]);

  // Cache management effect
  useEffect(() => {
    if (rxnCache) {
      localStorage.setItem(CACHE_KEYS.RXN_SEARCH, search);
      localStorage.setItem(CACHE_KEYS.RXN_TAG, JSON.stringify(tagCache));
    }

    if (adminRxnCache) {
      localStorage.setItem(CACHE_KEYS.ADMIN_RXN_SEARCH, search);
      localStorage.setItem(CACHE_KEYS.ADMIN_RXN_TAG, JSON.stringify(tagCache));
    }

    if (synCache) {
      localStorage.setItem(CACHE_KEYS.SYN_SEARCH, search);
    }
  }, [search, tagCache, rxnCache, adminRxnCache, synCache]);

  // Reset page when data changes
  useEffect(() => {
    setPage(1);
  }, [data]);

  // Pagination component
  const PaginationComponent = ({ className = "" }) => (
    <Navbar className={className}>
      <Navbar.Collapse className="justify-content-center align-middle">
        <Pagination style={{ marginBottom: "auto" }} aria-label="Table pagination">
          <Pagination.First
            onClick={() => setPage(1)}
            disabled={page === 1}
            aria-label="Go to first page"
          />
          <Pagination.Prev
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            aria-label="Go to previous page"
          />
          {paginationItems}
          <Pagination.Next
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            aria-label="Go to next page"
          />
          <Pagination.Last
            onClick={() => setPage(totalPages)}
            disabled={page === totalPages}
            aria-label="Go to last page"
          />
        </Pagination>
      </Navbar.Collapse>
    </Navbar>
  );

  // Loading state
  if (loading) {
    return <LoadingScreens />;
  }

  // No data state
  if (!data?.data?.length) {
    return (
      <div className="text-center p-4">
        <h3>No Data Available</h3>
        <p>There are no records to display at this time.</p>
      </div>
    );
  }

  const hasActions = actions && (actions.edit || actions.delete);
  const isClickable = actions?.view && actions?.viewLogic;

  return (
    <div className="rxn-table-container">
      {/* Filters and Controls */}
      <Navbar className="mb-3">
        <Navbar.Collapse>
          {tags.length > 0 && (
            <div className="me-3" style={{ minWidth: '200px' }}>
              <Select
                value={tagCache}
                options={tags}
                isMulti
                isSearchable
                placeholder="Filter by tags..."
                onChange={handleTagChange}
                aria-label="Filter by tags"
              />
            </div>
          )}

          <Dropdown className="me-2">
            <Dropdown.Toggle variant="outline-secondary" size="sm">
              {perPage}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {PER_PAGE_OPTIONS.map(option => (
                <Dropdown.Item
                  key={option}
                  onClick={() => handlePerPageChange(option)}
                  active={perPage === option}
                >
                  {option}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <span className="text-muted small">items per page</span>
        </Navbar.Collapse>

        <Navbar.Collapse className="justify-content-end">
          <Form.Group className="me-3">
            <Form.Control
              type="text"
              value={search}
              placeholder="Search records..."
              onChange={handleSearchChange}
              size="sm"
              aria-label="Search table"
            />
          </Form.Group>

          {actions?.add && actions?.addLogic && token && (
            <div>{actions.addLogic()}</div>
          )}
        </Navbar.Collapse>
      </Navbar>

      {/* Results Info */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <small className="text-muted">
          Showing {paginatedData.length} of {totalItems} results
          {search && ` for "${search}"`}
        </small>
        {totalItems > perPage && (
          <small className="text-muted">
            Page {page} of {totalPages}
          </small>
        )}
      </div>

      {/* Pagination - Top */}
      {totalPages > 1 && <PaginationComponent />}

      {/* Table */}
      <Table
        striped={striped}
        bordered={bordered}
        hover={hover}
        responsive
        className="mb-0"
      >
        <thead>
          <tr>
            {data.columns.map((col, index) => {
              const sortState = sortConfig.find((s) => s.key === col.field);

              return (
                <th
                  key={`header-${index}`}
                  scope="col"
                  style={{ cursor: col.sortable ? "pointer" : "default" }}
                  onClick={() => col.sortable && handleSort(col.field)}
                >
                  {col.label}{" "}
                  {col.sortable &&
                    (sortState ? (
                      sortState.direction === "asc" ? (
                        <FaSortUp />
                      ) : (
                        <FaSortDown />
                      )
                    ) : (
                      <FaSort className="text-muted" />
                    ))}
                </th>
              );
            })}
            {hasActions && <th scope="col">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {paginatedData.length > 0 ? (
            paginatedData.map((row, rowIndex) => (
              <tr
                key={`row-${rowIndex}`}
                onClick={() => handleRowClick(row)}
                style={{
                  cursor: isClickable ? "pointer" : "default"
                }}
                className={isClickable ? "table-row-clickable" : ""}
              >
                {renderTableCells(row, rowIndex)}
                {renderActionButtons(row, rowIndex)}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={data.columns.length + (hasActions ? 1 : 0)} className="text-center py-4">
                <div className="text-muted">
                  <strong>No results found</strong>
                  <br />
                  <small>Try adjusting your search or filter criteria</small>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Pagination - Bottom */}
      {totalPages > 1 && <PaginationComponent className="mt-3" />}

      <style jsx>{`
        .table-row-clickable:hover {
          background-color: var(--bs-table-hover-bg, rgba(0, 0, 0, 0.075));
        }
        
        .rxn-table-container .pagination {
          margin: 0;
        }
        
        .rxn-table-container .action {
          white-space: nowrap;
        }
      `}</style>
    </div>
  );
});

RxnTable.displayName = 'RxnTable';

export default RxnTable;